import { TeamSettingEntity } from '../../apis/team/team-setting-entity';
import { StagedAssociationContact } from '../../services/project-model-service';

export class TeamSettingViewModel {

    public static readonly defaultOption: string = 'Default';

    private _teamSettingEntity: TeamSettingEntity;
    private _onSuffixChange: () => void;
    public isDuplicate: boolean = false;
    // Full team name (association + suffix) when edit-teams loaded; null for teams added in this session.
    // Used to carry team renames over to the schedule rows, which reference teams by name.
    public originalName: string | null = null;

    constructor(entity: TeamSettingEntity, onSuffixChange?: () => void) {
        this._teamSettingEntity = entity;
        this._onSuffixChange = onSuffixChange ?? (() => undefined);
    }
    public toEntity(): TeamSettingEntity {
        return this._teamSettingEntity;
    }

    public get association(): string {
        return this._teamSettingEntity.association;
    }
    public set association(value: string) {
        this._teamSettingEntity.association = value;
    }

    public get suffix(): string {
        return this._teamSettingEntity.suffix;
    }
    public set suffix(value: string) {
        this._teamSettingEntity.suffix = value;
        this._onSuffixChange();
    }

    public get gameCount(): string {
        return this._teamSettingEntity.gameCount == null ? TeamSettingViewModel.defaultOption : this._teamSettingEntity.gameCount.toString();
    }
    public set gameCount(value: string) {
        this._teamSettingEntity.gameCount = value === TeamSettingViewModel.defaultOption ? null : parseInt(value, 10);
    }
}

export class TeamSettingListViewModel {
    private _association: string = null;
    public teamSettings: TeamSettingViewModel[] = [];
    public contacts: StagedAssociationContact[] = [];
    public isExpanded: boolean = false;
    // Name the association had when edit-teams loaded (null for associations added in this session),
    // used to detect renames so the persisted association can be renamed instead of orphaned.
    public originalAssociation: string | null = null;
    constructor(assocication: string, teamSettings: TeamSettingEntity[]) {
        this.association = assocication;
        teamSettings.forEach(t => this.addTeamSetting(t));
        this.updateDuplicateFlags();
    }

    public get association(): string {
        return this._association;
    }
    public set association(value: string) {
        this._association = value;
        this.teamSettings.forEach(teamSetting => {
            teamSetting.association = value;
        });
    }

    public get teamCount(): number {
        return this.teamSettings.length;
    }
    public get hasMultipleTeams(): boolean {
        return this.teamSettings.length > 1;
    }

    public get hasDuplicateSuffixes(): boolean {
        return this.teamSettings.some(t => t.isDuplicate);
    }

    public updateDuplicateFlags(): void {
        const counts = new Map<string, number>();
        this.teamSettings.forEach(t => {
            const key = t.suffix ?? '';
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        this.teamSettings.forEach(t => {
            t.isDuplicate = (counts.get(t.suffix ?? '') ?? 0) > 1;
        });
    }

    public addTeamSetting(teamSetting: TeamSettingEntity): void {
        this.teamSettings.push(new TeamSettingViewModel(teamSetting, () => this.updateDuplicateFlags()));
    }

    public addTeam(teamSetting: TeamSettingEntity = new TeamSettingEntity(this.association, '')): void {
        const newTeam = new TeamSettingEntity(
            teamSetting.association || this.association,
            teamSetting.suffix ?? '',
            teamSetting.gameCount ?? null
        );
        this.addTeamSetting(newTeam);
        this.updateDuplicateFlags();
    }

    public removeTeamSetting(teamSetting: TeamSettingEntity): void {
        const teamToRemove = this.teamSettings.find(t => t.association === teamSetting.association && t.suffix === teamSetting.suffix);
        if (teamToRemove) {
            this.teamSettings.splice(this.teamSettings.indexOf(teamToRemove), 1);
        }
        this.updateDuplicateFlags();
    }
}
